import { BaseMutations } from '@/store/base-mutations';
import { PaginationProps, TableOptions } from '@/types';
import { format_phone } from '@/utils';
import { Clients } from '@/modules/clients/types';
import { CatalogRequestFilters } from '@/modules/products/types';
import {
  CommercialOffer,
  CommercialOffers,
  Organization,
  CommercialOfferTemplate,
  NewCommercialOffer,
  NewCommercialOfferTemplate,
  OrganizationForFilters,
  Product,
  Staff,
} from '../types';
import CommercialOffersState from './state';
import {
  default_catalog_products_filters,
  default_new_offer,
  default_new_offer_template,
} from '../services';

export default class CommercialOffersMutations extends BaseMutations<CommercialOffersState> {
  SET_COMMERCIAL_OFFERS(offers: CommercialOffer[]) {
    this.state.commercial_offers = offers;
  }

  SET_CLIENTS_FOR_FILTERS(clients: string[]) {
    this.state.clients_for_filters = clients;
  }

  UPD_FILTER_KEY({ key, value }: { key: string, value: unknown }) {
    this.state.filters[key] = value;
  }

  UPD_OFFERS_FILTER_KEY({ key, value }: { key: string, value: unknown }) {
    this.state.offers_filters[key] = value;
  }

  CLEAR_OFFERS_FILTERS() {
    this.state.offers_filters = {
      search: '',
      dates: [],
      clients: [],
    };
  }

  TOGGLE_LOADING_OFFERS(value: boolean) {
    this.state.offers_loading = value;
  }

  TOGGLE_LOADING_OFFER_TEMPLATES(value: boolean) {
    this.state.offer_templates_loading = value;
  }

  UPD_NEW_OFFER_KEY({ key, value }: { key: keyof NewCommercialOffer, value: unknown }) {
    this.state.new_offer[key] = value;
  }

  UPD_NEW_OFFER_TEMPLATE_KEY({ key, value }: { key: keyof NewCommercialOfferTemplate, value: unknown }) {
    this.state.new_offer_template[key] = value;
  }

  SET_OFFERS_TEMPLATES(templates: CommercialOfferTemplate[]) {
    this.state.offers_templates = templates;
  }

  SET_ORGANIZATIONS_FOR_FILTERS(organizations: OrganizationForFilters[]) {
    this.state.organizations_for_filters = organizations;
  }

  SET_CURRENT_OFFER(offer: NewCommercialOffer) {
    this.state.current_offer = offer;
  }

  SET_CURRENT_OFFER_TEMPLATE(template: NewCommercialOfferTemplate) {
    this.state.current_offer_template = template;
  }

  SET_EDITING_OFFER() {
    this.state.new_offer = { ...this.state.current_offer };
  }

  SET_EDITING_OFFER_TEMPLATE() {
    this.state.new_offer_template = { ...this.state.current_offer_template };
  }

  CLEAR_NEW_OFFER_TEMPLATE() {
    this.state.new_offer_template = default_new_offer_template();
  }

  CLEAR_NEW_OFFER() {
    this.state.new_offer = default_new_offer();
  }

  ADD_LOGO_FILE(file: { id: number, title: string, file: File | null, is_new_file?: boolean }) {
    this.state.new_offer_template.files.push(file);
  }

  UPD_ORGANIZATIONS(organizationId: number) {
    const organizationIndex = this.state.new_offer_template.organizations.findIndex((organization: Organization) => organization.id === organizationId);

    if (organizationIndex >= 0) {
      this.state.new_offer_template.organizations.splice(organizationIndex, 1);
    } else {
      this.state.new_offer_template.organizations.push({ id: organizationId });
    }
  }

  DELETE_LOGO_FILE(id: number) {
    this.state.new_offer_template.files = this.state.new_offer_template.files.filter((file) => {
      const is_file_to_delete = file.id === id;

      if (is_file_to_delete && !file.is_new_file) {
        this.state.logos_to_remove.push(file.id);
      }
      return !is_file_to_delete;
    });

    if (this.state.new_offer_template.files.length === 0) {
      return;
    }

    const has_active_file = this.state.new_offer_template.files.some((file) => file.is_active);

    if (!has_active_file) {
      this.state.new_offer_template.files[0].is_active = true;
    }
  }

  SET_TABLE_OPTIONS(opts: TableOptions) {
    this.state.table_options = opts;
  }

  SET_OFFERS_TABLE_OPTIONS(opts: TableOptions) {
    this.state.table_options_offers_list = opts;
  }

  UPD_OFFERS_TABLE_OPTIONS({ key, value }: { key: keyof TableOptions, value: unknown }) {
    this.state.table_options_offers_list[key] = value;
  }

  SET_CLIENT_TO_OFFER(client: Clients.ClientToAddItem | null) {
    if (client === null) {
      this.state.new_offer.client_id = 0;
      this.state.new_offer.client_name = '';
      this.state.new_offer.client_contact_full_name = '';
      this.state.new_offer.client_contact_phone = '';
      this.state.new_offer.client_contact_email = '';
    } else {
      this.state.new_offer.client_id = Number(client.id);
      this.state.new_offer.client_name = client.organization_name;

      if (client.contact !== null) {
        this.state.new_offer.client_contact_full_name = client.contact.contact_person;
        this.state.new_offer.client_contact_phone = format_phone(client.contact.telephone);

        if (client.contact.email != null) {
          this.state.new_offer.client_contact_email = client.contact.email;
        }
      }
    }
  }

  ADD_STAFF(staff: Staff) {
    const index = this.state.new_offer.users.findIndex((user) => user.user_id === staff.user_id);

    if (index === -1 || staff.new_staff) {
      this.state.new_offer.users.push(staff);
    } else {
      this.state.new_offer.users.splice(index, 1, staff);
    }
  }

  DELETE_STAFF(local_id: number) {
    this.state.new_offer.users = this.state.new_offer.users.filter((staff) => staff.local_id !== local_id);
  }

  UPD_ACTIVE_FILE(id: number) {
    this.state.current_offer_template.files = this.state.current_offer_template.files.map((file) =>
      ({
        ...file,
        is_active: file.id === id,
      }));
  }

  ADD_PRODUCT(product: Product) {
    this.state.new_offer.products.unshift(product);
  }

  DELETE_SELECTED_PRODUCT(product: Product) {
    this.state.new_offer.products = this.state.new_offer.products.filter(
      (p: Product) =>
        p.product_id !== product.product_id
        || p.is_partner_product !== product.is_partner_product,
    );
  }

  UPD_PRODUCT_QTY({ product, quantity }: { product: Product, quantity: string|number }) {
    product.quantity = Number(quantity);
    product.amount = product.quantity * Number(product.client_price);
  }

  SET_CLIENT_PRICE_AND_DISCOUNT({ product, value, field }: { product: Product, value: number, field: string }) {
    this.state.new_offer.products = this.state.new_offer.products.map((p) => {
      if (p.product_id === product.product_id && p.is_partner_product === product.is_partner_product) {
        let discount = Number(product.discount) || 0;
        let client_price = Number(product.client_price) || product.price;

        // расчет скидки если меняем цену клиента
        if (field === 'client_price') {
          client_price = Number(value);

          if (client_price >= product.price) {
            discount = 0;
          } else {
            const discount_without_rounding = ((product.price - client_price) / product.price) * 100;

            // округляем до 4 знаков
            discount = Math.round(discount_without_rounding * 10000) / 10000;
          }
        }

        // расчет цены клиента если меняем скидку
        if (field === 'discount') {
          discount = Number(value);
          client_price = product.price - (product.price * (value / 100));
        }

        const amount = client_price * product.quantity;

        return {
          ...p,
          client_price,
          amount,
          discount,
        };
      }
      return p;
    });
  }

  SET_DESCRIPTION({ product, description, field }: { product: Product, description: string, field: string }) {
    this.state.new_offer.products = this.state.new_offer.products.map((p) => {
      if (p.product_id === product.product_id && p.is_partner_product === product.is_partner_product) {
        if (field === 'description') {
          return {
            ...p,
            description,
          };
        }
      }
      return p;
    });
  }

  SET_PRODUCT_IMG({ product, file, field }: { product: Product, file: File | null, field: string }) {
    this.state.new_offer.products = this.state.new_offer.products.map((p) => {
      if (p.product_id === product.product_id && p.is_partner_product === product.is_partner_product) {
        if (field === 'file') {
          return {
            ...p,
            file,
          };
        }
      }
      return p;
    });
  }

  CLEAR_REMOVAL_LOGOS() {
    this.state.logos_to_remove = [];
  }

  UPD_ACTIVE_TAB(value: number) {
    this.state.active_tab = value;
  }

  UPD_PAGINATION_OFFERS_FIELD({ key, value }: { key: keyof PaginationProps, value: number }) {
    this.state.pagination_offers_list[key] = value;
  }

  SET_HAS_TEMPLATE(value: boolean) {
    this.state.has_template = value;
  }

  SET_CATALOG_PRODUCTS_TABLE_OPTIONS(opts: TableOptions) {
    this.state.catalog_products_table_options = opts;
  }

  UPD_CATALOG_PRODUCTS_TABLE_OPTIONS({ key, value }: { key: keyof TableOptions, value: unknown }) {
    this.state.catalog_products_table_options[key] = value;
  }

  UPD_CATALOG_PRODUCTS_PAGINATION_FIELD({ key, value }: { key: keyof PaginationProps, value: number }) {
    this.state.catalog_products_pagination_props[key] = value;
  }

  SET_CATALOG_PRODUCTS(catalog_products: CommercialOffers.CatalogProduct[]) {
    this.state.catalog_products = catalog_products;
  }

  SET_CATALOG_PRODUCTS_LOADING(value: boolean) {
    this.state.catalog_products_loading = value;
  }

  UPD_CATALOG_PRODUCT_FILTER_KEY({ key, value }: { key: keyof CatalogRequestFilters, value: unknown }) {
    this.state.catalog_products_filters[key] = value;
  }

  CLEAR_CATALOG_PRODUCT_FILTERS() {
    this.state.catalog_products_filters = default_catalog_products_filters();
  }
}
